import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { FileText, Percent, Info, Calendar, Download } from 'lucide-react';
import ReportDateRangeFilter from './ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { downloadCsv } from '@/utils/csv';
import ReportExportButton from '@/components/reports/ReportExportButton';

interface VatSummaryData {
  company_info: {
    name: string;
    vat_number: string;
    currency: string;
    country: string;
    tax_authority: string;
    prices_include_tax?: boolean;
  };
  period?: { from_date: string; to_date: string };
  summary: {
    standard_rated_sales: number;
    reduced_rated_sales?: number;
    zero_rated_sales: number;
    exempt_sales: number;
    out_of_scope_sales: number;
    standard_rated_vat?: number;
    reduced_rated_vat?: number;
    output_vat: number;
    vat_reversed: number;
    net_output_vat: number;
    input_vat?: number;
    net_vat_due?: number;
    sales_ex_vat?: number;
    purchases_ex_vat?: number;
  };
  hmrc_boxes?: {
    box_1: number;
    box_2: number;
    box_3: number;
    box_4: number;
    box_5: number;
    box_6: number;
    box_7: number;
    box_8: number;
    box_9: number;
    notes?: Record<string, string>;
  };
}

const HMRC_BOX_LABELS: { key: keyof NonNullable<VatSummaryData['hmrc_boxes']>; label: string; hint: string }[] = [
  { key: 'box_1', label: 'Box 1', hint: 'VAT due on sales and other outputs' },
  { key: 'box_2', label: 'Box 2', hint: 'VAT due on acquisitions from EU (not tracked)' },
  { key: 'box_3', label: 'Box 3', hint: 'Total VAT due (Box 1 + Box 2)' },
  { key: 'box_4', label: 'Box 4', hint: 'VAT reclaimed on purchases (input VAT)' },
  { key: 'box_5', label: 'Box 5', hint: 'Net VAT to pay or reclaim' },
  { key: 'box_6', label: 'Box 6', hint: 'Total value of sales excluding VAT' },
  { key: 'box_7', label: 'Box 7', hint: 'Total value of purchases excluding VAT' },
  { key: 'box_8', label: 'Box 8', hint: 'Total value of supplies to EU (not tracked)' },
  { key: 'box_9', label: 'Box 9', hint: 'Total value of acquisitions from EU (not tracked)' },
];

export default function VatReturnSummarySection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['vatReturnSummary', from_date, to_date],
    queryFn: async () => {
      const res = await reportsApi.getVatReturnSummary({ from_date, to_date });
      return res as VatSummaryData;
    },
    enabled: !!from_date && !!to_date,
  });

  const c = data?.company_info;
  const s = data?.summary;
  const boxes = data?.hmrc_boxes;
  const currency = c?.currency || 'GBP';
  const isHmrc = (c?.tax_authority || '').toUpperCase() === 'HMRC' || ['GB', 'UK'].includes((c?.country || '').toUpperCase());

  const handleExportBreakdown = () => {
    if (!s || !c) return;
    downloadCsv(
      `VAT_Return_Summary_${from_date}_to_${to_date}.csv`,
      [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' },
      ],
      [
        { field: 'Company', value: c.name },
        { field: 'VAT Number', value: c.vat_number || '' },
        { field: 'Period From', value: from_date },
        { field: 'Period To', value: to_date },
        { field: 'Currency', value: currency },
        { field: 'Standard Rated Sales (ex VAT)', value: (s.standard_rated_sales || 0).toFixed(2) },
        { field: 'Reduced Rated Sales (ex VAT)', value: (s.reduced_rated_sales || 0).toFixed(2) },
        { field: 'Zero-Rated Sales (ex VAT)', value: (s.zero_rated_sales || 0).toFixed(2) },
        { field: 'Output VAT', value: (s.output_vat || 0).toFixed(2) },
        { field: 'VAT Reversed (Returns)', value: (s.vat_reversed || 0).toFixed(2) },
        { field: 'Net Output VAT', value: (s.net_output_vat || 0).toFixed(2) },
        { field: 'Input VAT', value: (s.input_vat || 0).toFixed(2) },
        { field: 'Net VAT Due', value: (s.net_vat_due ?? s.net_output_vat ?? 0).toFixed(2) },
      ],
    );
  };

  const handleExportHmrcBoxes = () => {
    if (!boxes || !c) return;
    downloadCsv(
      `HMRC_VAT_Boxes_${from_date}_to_${to_date}.csv`,
      [
        { key: 'box', label: 'Box' },
        { key: 'description', label: 'Description' },
        { key: 'amount', label: 'Amount' },
      ],
      HMRC_BOX_LABELS.filter((row) => row.key !== 'notes').map((row) => ({
        box: row.label,
        description: row.hint,
        amount: Number(boxes[row.key] ?? 0).toFixed(2),
      })),
    );
  };

  return (
    <div className="space-y-6">
      <ReportDateRangeFilter
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
      />

      {!from_date || !to_date ? (
        <div className="card p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Select Date Range</h3>
          <p className="text-gray-500">Please select a date range to generate the VAT report.</p>
        </div>
      ) : isLoading ? (
        <div className="card p-12 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="card p-8 text-center text-red-500">
          Failed to load VAT report.
        </div>
      ) : s && c ? (
        <>
          <div className="card p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-lg overflow-hidden relative">
            <div className="absolute right-0 top-0 opacity-10">
              <FileText className="w-48 h-48 -mr-10 -mt-10" />
            </div>

            <div className="relative z-10 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold">{c.country} VAT Return Summary</h2>
                  <p className="text-slate-400 mt-1">{c.name}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ReportExportButton onClick={handleExportBreakdown} label="Export summary CSV" />
                  {boxes && (
                    <button
                      type="button"
                      onClick={handleExportHmrcBoxes}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    >
                      <Download className="w-4 h-4" />
                      Export HMRC boxes CSV
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-700/50">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">VAT Number</p>
                  <p className="font-medium text-slate-100">{c.vat_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Tax Authority</p>
                  <p className="font-medium text-slate-100">{c.tax_authority}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Currency</p>
                  <p className="font-medium text-slate-100">{c.currency}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Period</p>
                  <p className="font-medium text-slate-100 text-sm">
                    {from_date} → {to_date}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card p-5">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm font-medium text-gray-500">Standard Rated Sales</p>
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Percent className="w-4 h-4" /></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{formatMoney(s.standard_rated_sales, { currency })}</h3>
              <p className="text-xs text-gray-500 mt-1">ex VAT</p>
            </div>

            <div className="card p-5">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm font-medium text-gray-500">Reduced / Zero-Rated</p>
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Percent className="w-4 h-4" /></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {formatMoney((s.reduced_rated_sales || 0) + s.zero_rated_sales + s.exempt_sales, { currency })}
              </h3>
              <p className="text-xs text-gray-500 mt-1">ex VAT</p>
            </div>

            <div className="card p-5">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm font-medium text-gray-500">Output VAT</p>
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Percent className="w-4 h-4" /></div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{formatMoney(s.output_vat, { currency })}</h3>
            </div>

            <div className="card p-5">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm font-medium text-gray-500">Net VAT Due</p>
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Percent className="w-4 h-4" /></div>
              </div>
              <h3 className="text-2xl font-bold text-indigo-700">
                {formatMoney(s.net_vat_due ?? s.net_output_vat, { currency })}
              </h3>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">VAT Breakdown</h3>
            </div>
            <div className="divide-y divide-gray-200">
              <div className="px-6 py-4 flex justify-between items-center hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">Standard Rated Sales</p>
                  <p className="text-sm text-gray-500">Typically 20% VAT</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatMoney(s.standard_rated_sales, { currency })}</p>
                  {s.standard_rated_vat != null && (
                    <p className="text-xs text-gray-500">VAT {formatMoney(s.standard_rated_vat, { currency })}</p>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 flex justify-between items-center hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">Reduced Rated Sales</p>
                  <p className="text-sm text-gray-500">Typically 5% VAT</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatMoney(s.reduced_rated_sales || 0, { currency })}</p>
                  {s.reduced_rated_vat != null && (
                    <p className="text-xs text-gray-500">VAT {formatMoney(s.reduced_rated_vat, { currency })}</p>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 flex justify-between items-center hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">Zero-Rated / Exempt Sales</p>
                  <p className="text-sm text-gray-500">0% VAT</p>
                </div>
                <p className="font-semibold text-gray-900">
                  {formatMoney(s.zero_rated_sales + s.exempt_sales + s.out_of_scope_sales, { currency })}
                </p>
              </div>
              <div className="px-6 py-4 flex justify-between items-center bg-gray-50 border-t-2 border-gray-200">
                <p className="font-bold text-gray-900">Total Output VAT</p>
                <p className="font-bold text-gray-900">{formatMoney(s.output_vat, { currency })}</p>
              </div>
              <div className="px-6 py-4 flex justify-between items-center hover:bg-gray-50 text-red-600">
                <div>
                  <p className="font-medium">VAT Reversed (Returns)</p>
                  <p className="text-sm text-red-500">Credit notes / sales returns</p>
                </div>
                <p className="font-semibold">-{formatMoney(s.vat_reversed, { currency })}</p>
              </div>
              <div className="px-6 py-4 flex justify-between items-center hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">Input VAT (Purchases)</p>
                  <p className="text-sm text-gray-500">From purchase invoices</p>
                </div>
                <p className="font-semibold text-gray-900">{formatMoney(s.input_vat || 0, { currency })}</p>
              </div>
              <div className="px-6 py-5 flex justify-between items-center bg-indigo-50 border-t-2 border-indigo-100">
                <p className="text-lg font-bold text-indigo-900">Net VAT Payable / (Reclaimable)</p>
                <p className="text-xl font-bold text-indigo-700">
                  {formatMoney(s.net_vat_due ?? s.net_output_vat, { currency })}
                </p>
              </div>
            </div>
          </div>

          {boxes && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h3 className="font-semibold text-gray-900">
                  {isHmrc ? 'HMRC VAT Return Boxes' : 'VAT Return Boxes'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Figures ready for MTD bridging software. Export CSV and enter into your filing tool.
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {HMRC_BOX_LABELS.map((row) => (
                  <div key={row.key} className="px-6 py-3.5 flex justify-between items-center gap-4 hover:bg-gray-50">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{row.label}</p>
                      <p className="text-sm text-gray-500 truncate">{row.hint}</p>
                    </div>
                    <p className="font-semibold text-gray-900 shrink-0 tabular-nums">
                      {formatMoney(Number(boxes[row.key] ?? 0), { currency })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Bridging export (not direct HMRC submit)</p>
              <p className="text-sm text-blue-700 mt-1">
                Use the CSV exports with HMRC-compatible bridging software. Boxes 2, 8 and 9 are not tracked
                in SaleDesk yet — adjust those in your bridging tool if needed. Always check figures against
                your records before filing.
              </p>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
